'use client';

import NiceModal from '@ebay/nice-modal-react';
import { useRouter } from '@/i18n/navigation';
import { NotificationBannerRes, NotificationItem } from '@/features/notifications/services';
import { NotificationEventType } from '@/features/notifications/constant';
import RechargeDetailModal from '@/features/wallet/components/recharge-detail-modal';
import { useGetStaticResource } from '@/services/static-resource';
import { showWithdrawalDetailModal, WithdrawalType } from '@/features/wallet/components/withdrawal-detail-modal';
import { menuTabUrl, activityPage } from "@/const/page-url";
import { jumpByUrl } from '@/utils/jump-url';
import { ActivitySourceEnum } from '@/const/report';

export function useNotificationJump(onJumped?: (detail: NotificationBannerRes | NotificationItem) => void) {
  const router = useRouter();
  const { data: staticResource } = useGetStaticResource();

  const notifyJumped = (detail: NotificationBannerRes | NotificationItem) => {
    onJumped?.(detail);
  };

  // 判断货币类型：有 region 是法币，没有是数字货币
  function getCurrencyType(currency: string): WithdrawalType {
    const currencyInfo = staticResource?.currency?.[currency];
    const isFiat = !!currencyInfo?.region;
    return isFiat ? WithdrawalType.Cash : WithdrawalType.DigitalCurrency;
  }

  // 平台消息跳转
  const jumpPlatform = (detail: NotificationBannerRes | NotificationItem) => {
    const links = detail.button_links;
    if (Array.isArray(links) && links.length > 0) {
      const link = links[0].button_link;
      notifyJumped(detail);
      if (link) {
        jumpByUrl(link, router);
      }
    }
  };

  // 充值详情
  const jumpRecharge = (detail: NotificationBannerRes | NotificationItem) => {
    if (!detail.order_id) return;

    const type = detail.currency
      ? getCurrencyType(detail.currency)
      : 1;

    NiceModal.show(RechargeDetailModal, {
      order_id: detail.order_id,
      type,
    });
    notifyJumped(detail);
  };

  // 提现详情
  const jumpWithdraw = (detail: NotificationBannerRes | NotificationItem) => {
    if (!detail.order_id) return;

    const type = detail.currency
      ? getCurrencyType(detail.currency)
      : WithdrawalType.Cash;

    showWithdrawalDetailModal({
      order_id: detail.order_id,
      type,
    });
    notifyJumped(detail);
  };

  // 代理
  const jumpProxy = (detail: NotificationBannerRes | NotificationItem) => {
    const subtype = detail.event_sub_type;
    // 代理事件中需要跳转的子类型事件：1=代理派奖，4=代理邀请奖励到账，5-临时被邀请奖励赠送
    const types = [1, 4];
    if (types.includes(subtype)) {
      notifyJumped(detail);
      router.push('/affiliate');
    } else if (subtype === 5) {
      notifyJumped(detail);
      // router.push('/fantasy');
      router.push(menuTabUrl.fantasy);
    }
  };

  // VIP
  const jumpVIP = (detail: NotificationBannerRes | NotificationItem) => {
    notifyJumped(detail);
    router.push('/reward');
  };

  // Rummy
  const jumpRummy = (detail: NotificationBannerRes | NotificationItem) => {
    notifyJumped(detail);
    router.push(menuTabUrl.rummy);
  };

  // 累充
  const jumpCumulativeDeposit = (detail: NotificationBannerRes | NotificationItem) => {
    const id = detail.jump_params ? JSON.parse(detail.jump_params)?.id : '';
    if (id) {
      notifyJumped(detail);
      router.push(`${activityPage.cumulativeDeposit}?id=${id}&trackSource=${ActivitySourceEnum.inbox}`);
    }
  };

  // 总分发函数
  const jumpByEventType = (detail?: NotificationBannerRes | NotificationItem) => {
    if (!detail) return;

    const type = detail.event_type;

    switch (type) {
      case NotificationEventType.Platform:
        jumpPlatform(detail);
        break;

      case NotificationEventType.Recharge:
        jumpRecharge(detail);
        break;

      case NotificationEventType.Withdraw:
        jumpWithdraw(detail);
        break;

      case NotificationEventType.Proxy:
        jumpProxy(detail);
        break;

      case NotificationEventType.VIP:
        jumpVIP(detail);
        break;

      case NotificationEventType.Rummy:
        jumpRummy(detail);
        break;

      case NotificationEventType.CumulativeDeposit:
        jumpCumulativeDeposit(detail);
        break;

      default:
        break;
    }
  };

  return {
    jumpByEventType,
  };
}
